import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        // Not an admin — sign them out immediately
        try { await api.post("/auth/logout"); } catch {}
        setError("This account does not have admin access.");
        setLoading(false);
        return;
      }
      toast.success("Welcome back, admin");
      nav(loc.state?.from || "/admin");
    } catch (err) { setError(formatError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center font-display font-bold tracking-tight text-lg text-ink-900" data-testid="admin-login-brand">
          NIRAJ <span className="text-gold">BHAARDWAJ</span>
        </Link>
        <div className="mt-3 text-center overline">Restricted Access</div>
        <div className="glass rounded-2xl p-8 mt-6 space-y-5 relative">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-gold" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold tracking-tight text-ink-900">Admin Portal</h1>
              <p className="text-xs text-ink-600">Authorised personnel only.</p>
            </div>
          </div>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
            <Field label="Admin email" type="email" value={email} onChange={setEmail} testid="admin-login-email" />
            <Field label="Password" type="password" value={password} onChange={setPassword} testid="admin-login-password" />
            {error && <div className="text-sm text-red-600 flex items-center gap-2" data-testid="admin-login-error"><Lock className="w-3.5 h-3.5" /> {error}</div>}
            <button type="submit" disabled={loading} className="btn-neon w-full disabled:opacity-60" data-testid="admin-login-submit">
              {loading ? "Verifying..." : "Sign in to admin"}
            </button>
            <div className="text-center">
              <Link to="/admin/forgot-password" className="text-xs text-ink-600 hover:text-gold underline underline-offset-4" data-testid="admin-forgot-link">Forgot password?</Link>
            </div>
          </form>
        </div>
        <p className="mt-6 text-center text-xs text-ink-500">This portal is monitored and restricted.</p>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <label className="block">
    <div className="overline mb-2 !text-ink-600">{label}</div>
    <input type={type} value={value} required onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg bg-cream-50 border border-ink-900/15 px-4 py-3 outline-none focus:border-gold text-ink-900" data-testid={testid} />
  </label>
);
